import React from 'react';
import { Crown, Star, TrendingUp, Shield, Calendar, Award, CheckCircle, Users, BarChart3, Gift, Briefcase, Music } from 'lucide-react';
import Button from '../../../components/ui/Button';

const PremiumFeatures = ({
  currentSubscription,
  subscriptionPlans,
  reputationScore,
  verificationBadges,
  onUpgrade
}) => {
  const academyModules = [
    {
      id: 'marketing',
      title: 'Marketing Musical',
      duration: '4 semaines',
      lessons: 16,
      icon: <TrendingUp className="w-5 h-5" />,
      price: 'Inclus Premium',
      topics: ['Stratégie digitale', 'Réseaux sociaux', 'Brand building', 'Analytics']
    },
    {
      id: 'production',
      title: 'Production & Studio',
      duration: '6 semaines',
      lessons: 24,
      icon: <Music className="w-5 h-5" />,
      price: 'Inclus Premium',
      topics: ['Techniques studio', 'Mixage', 'Mastering', 'Home studio']
    },
    {
      id: 'career',
      title: 'Gestion de Carrière',
      duration: '3 semaines',
      lessons: 12,
      icon: <Briefcase className="w-5 h-5" />,
      price: 'Inclus Premium',
      topics: ['Planning carrière', 'Négociation', 'Networking', 'Développement']
    },
    {
      id: 'legal',
      title: 'Aspects Juridiques',
      duration: '5 semaines',
      lessons: 20,
      icon: <Shield className="w-5 h-5" />,
      price: 'Premium+',
      topics: ['Droits d\'auteur', 'Contrats', 'SACEM', 'Protection œuvres']
    }
  ];

  const affiliateSystem = {
    commission_rate: '15%',
    min_payout: '50€',
    payment_frequency: 'Mensuel',
    specializations: [
      { name: 'Jazz & Blues', rate: '18%', demand: 'Élevée' },
      { name: 'Rock & Metal', rate: '15%', demand: 'Moyenne' },
      { name: 'Électronique', rate: '20%', demand: 'Très élevée' },
      { name: 'Classique', rate: '16%', demand: 'Moyenne' }
    ]
  };

  const premiumBenefits = {
    communication: [
      'Messages illimités chiffrés E2E',
      'Appels vidéo sécurisés intégrés',
      'Partage de fichiers avec watermark',
      'Traduction temps réel (12 langues)',
      'Historique messages permanent'
    ],
    booking: [
      'Négociation automatisée de contrats',
      'Signature électronique légale',
      'Calendrier partagé temps réel',
      'Système de paiement intégré',
      'Assurance événement incluse'
    ],
    networking: [
      'Accès réseau exclusif verified pro',
      'Événements privés Industry VIP',
      'Matchmaking IA personnalisé',
      'Groupes de collaboration fermés',
      'Mentorship avec experts industrie'
    ],
    analytics: [
      'Dashboards performance détaillés',
      'Analytics audience cross-platform',
      'Prédictions tendances marché',
      'ROI tracking complet',
      'Rapports mensuels personnalisés'
    ]
  };

  // getCurrentPlanFeatures supprimé (unused)

  const renderSubscriptionCard = (planType, planData) => {
    const isCurrentPlan = currentSubscription === planType;
    const isUpgrade = ['pro', 'business', 'venue']?.includes(planType) && currentSubscription === 'free';

    return (
      <div className={`border-2 rounded-lg p-6 relative ${
        isCurrentPlan 
          ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
      }`}>
        {isCurrentPlan && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
              Plan Actuel
            </span>
          </div>
        )}

        <div className="text-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{planData?.name}</h3>
          <div className="flex items-center justify-center space-x-2 mb-4">
            {planType !== 'free' && <Crown className="w-5 h-5 text-yellow-500" />}
            <span className="text-2xl font-bold text-gray-900">
              {planType === 'free' ? 'Gratuit' : 
               planType === 'pro' ? '29€/mois' :
               planType === 'business'? '89€/mois' : '49€/mois'}
            </span>
          </div>
        </div>

        <div className="space-y-3 mb-6">
          {planData?.features?.map((feature, index) => (
            <div key={index} className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-700 dark:text-neutral-300">{feature}</span>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          {!isCurrentPlan && (
            <Button
              variant={isUpgrade ? "default" : "outline"}
              className="w-full"
              onClick={() => onUpgrade(planType)}
            >
              {isUpgrade ? 'Passer Premium' : 'Changer de plan'}
            </Button>
          )}
          
          {isCurrentPlan && (
            <div className="text-center">
              <span className="text-sm text-blue-600 font-medium">Plan actif</span>
            </div>
          )}
        </div>

        {/* Plan-specific highlights */}
        {planType === 'business' && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <div className="inline-flex items-center space-x-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium">
                <Gift className="w-3 h-3" />
                <span>Commission affiliation 15%</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Current Status Banner */}
  <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-pink-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">
                Écosystème Premium Artis.io
              </h2>
              <p className="text-gray-600 dark:text-neutral-400">
                Communication sécurisée • Booking automatisé • Formation continue • Affiliation
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center space-x-2 mb-2">
              <Star className="w-5 h-5 text-yellow-500" />
              <span className="text-lg font-semibold text-gray-900">
                {reputationScore?.toFixed(1)}
              </span>
            </div>
            <div className="flex space-x-1">
              {verificationBadges?.map((badge, index) => (
                <CheckCircle key={index} className="w-4 h-4 text-blue-500" />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Plans */}
      <div>
  <h2 className="text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-6">Choisissez Votre Plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(subscriptionPlans)?.map(([planType, planData]) => 
            renderSubscriptionCard(planType, planData)
          )}
        </div>
      </div>

      {/* Premium Features Showcase */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-neutral-100 mb-4">
            Communication Sécurisée Premium
          </h3>
          <div className="space-y-3">
            {premiumBenefits?.communication?.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Shield className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                <span className="text-sm text-gray-700 dark:text-neutral-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Booking & Négociation Avancés
          </h3>
          <div className="space-y-3">
            {premiumBenefits?.booking?.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Calendar className="w-4 h-4 text-blue-500 flex-shrink-0 mt-1" />
                <span className="text-sm text-gray-700 dark:text-neutral-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Réseau Professionnel Exclusif
          </h3>
          <div className="space-y-3">
            {premiumBenefits?.networking?.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Users className="w-4 h-4 text-purple-500 flex-shrink-0 mt-1" />
                <span className="text-sm text-gray-700 dark:text-neutral-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Analytics & Performance
          </h3>
          <div className="space-y-3">
            {premiumBenefits?.analytics?.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <BarChart3 className="w-4 h-4 text-orange-500 flex-shrink-0 mt-1" />
                <span className="text-sm text-gray-700 dark:text-neutral-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Artis.io Academy */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-8 border border-green-200">
        <div className="text-center mb-8">
          <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Artis.io Academy
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Formation professionnelle continue avec certifications reconnues par l&apos;industrie musicale
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {academyModules?.map((module) => (
            <div key={module?.id} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    {module?.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{module?.title}</h4>
                    <p className="text-sm text-gray-600">{module?.duration} • {module?.lessons} cours</p>
                  </div>
                </div>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                  {module?.price}
                </span>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {module?.topics?.map((topic, index) => (
                  <span key={index} className="text-xs bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-neutral-300 px-2 py-1 rounded">
                    {topic}
                  </span>
                ))}
              </div>
              
              <Button variant="outline" size="sm" className="w-full">
                Commencer le Module
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Affiliate System */}
      <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-8 border border-orange-200">
        <div className="text-center mb-8">
          <Gift className="w-12 h-12 text-orange-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Système d&apos;Affiliation Artis.io
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Gagnez des commissions en recommandant des artistes et en parrainant de nouveaux membres Premium
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-orange-200 rounded-lg p-4 text-center">
            <TrendingUp className="w-8 h-8 text-orange-600 mx-auto mb-3" />
            <h4 className="font-semibold text-gray-900 mb-2">Commission Booking</h4>
            <span className="text-2xl font-bold text-orange-600">{affiliateSystem?.commission_rate}</span>
            <p className="text-sm text-gray-600 mt-1">Sur chaque booking réussi</p>
          </div>
          
          <div className="bg-white border border-orange-200 rounded-lg p-4 text-center">
            <Users className="w-8 h-8 text-orange-600 mx-auto mb-3" />
            <h4 className="font-semibold text-gray-900 mb-2">Paiement Minimum</h4>
            <span className="text-2xl font-bold text-orange-600">{affiliateSystem?.min_payout}</span>
            <p className="text-sm text-gray-600 mt-1">Seuil de versement</p>
          </div>
          
          <div className="bg-white border border-orange-200 rounded-lg p-4 text-center">
            <Calendar className="w-8 h-8 text-orange-600 mx-auto mb-3" />
            <h4 className="font-semibold text-gray-900 mb-2">Fréquence</h4>
            <span className="text-xl font-bold text-orange-600">{affiliateSystem?.payment_frequency}</span>
            <p className="text-sm text-gray-600 mt-1">Paiements automatiques</p>
          </div>
        </div>

        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">
            Commission par Spécialisation
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {affiliateSystem?.specializations?.map((spec, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{spec?.name}</span>
                  <span className="text-lg font-bold text-orange-600">{spec?.rate}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Demande:</span>
                  <span className={`text-sm font-medium ${
                    spec?.demand === 'Très élevée' ? 'text-red-600' :
                    spec?.demand === 'Élevée'? 'text-orange-600' : 'text-blue-600'
                  }`}>
                    {spec?.demand}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      {currentSubscription === 'free' && (
        <div className="text-center bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-8 text-white">
          <Crown className="w-16 h-16 mx-auto mb-4 text-yellow-300" />
          <h2 className="text-3xl font-bold mb-4">
            Prêt à Passer Premium ?
          </h2>
          <p className="text-xl mb-6 text-blue-100">
            Débloquez tout l&apos;écosystème Artis.io et développez votre carrière musicale
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => onUpgrade('pro')}
              className="bg-white text-blue-600 hover:bg-gray-100"
            >
              Essai Gratuit 7 jours
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-blue-600"
              onClick={() => {/* TODO: open demo modal */}}
            >
              Démonstration personnalisée
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumFeatures;